import {CONTEXT_CHANGED_EVENT} from "../globals";
import type {CommonOrgEntity} from "../backend";

/** Distinguishes a first-load default from a deliberate user choice (AD-10). */
export type ContextChangeSource = "user-selection" | "default";

/** `detail` payload of the `bridge-bar-context-changed` event (contracts.md §1). */
export interface BridgeBarContextChangedDetail {
  commonOrgId: string;
  displayName: string;
  source: ContextChangeSource;
}

const STORAGE_KEY_PREFIX = "bridge-bar-context-selection";

/**
 * Identifies whose selection this is, and for which login.
 *
 * `platformId` keeps a second user in the same tab from reading the first
 * user's selection (AD-9). `sessionId` scopes the entry to one bridge login, so
 * signing out and back in no longer restores the previous choice — the entry
 * from the old session is simply never found and the first-alphabetical default
 * applies. sessionStorage alone could not express this: it is cleared when the
 * tab closes, not when the session ends, so a stale selection survived logout.
 */
export interface SelectionScope {
  platformId: string;
  sessionId: string;
}

/**
 * sessionStorage key for one user's selection within one login. sessionStorage
 * (not localStorage) keeps the selection per-tab (FR-3 independence); the
 * composite key adds the per-login scoping described on {@link SelectionScope}.
 */
function storageKey({platformId, sessionId}: SelectionScope): string {
  return `${STORAGE_KEY_PREFIX}:${platformId}:${sessionId}`;
}

/**
 * Drop this user's entries from superseded sessions. Without this, each
 * logout/login in a long-lived tab would leave behind an orphaned entry that is
 * never read again. Scoped to the same `platformId` so a different user's
 * selection in the same storage is left alone.
 */
function pruneSupersededSessions(scope: SelectionScope, currentKey: string) {
  const userPrefix = `${STORAGE_KEY_PREFIX}:${scope.platformId}:`;
  const superseded: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(userPrefix) && key !== currentKey) {
      superseded.push(key);
    }
  }
  superseded.forEach(key => sessionStorage.removeItem(key));
}

/** Persist the selected org for this user and login, per-tab. Best-effort. */
export function saveSelection(
  scope: SelectionScope,
  commonOrgId: string
): void {
  try {
    const key = storageKey(scope);
    pruneSupersededSessions(scope, key);
    sessionStorage.setItem(key, commonOrgId);
  } catch {
    // sessionStorage unavailable (private mode / disabled): the selection
    // simply won't persist across reloads — not fatal to the selector.
  }
}

/**
 * Remove every stored selection for this user, across all logins.
 *
 * Used when persistence is switched off (see `features.dev.orgContextPersistence`)
 * so the no-persistence demo genuinely leaves nothing in sessionStorage — an
 * orphaned key visible in devtools would undercut the point being demonstrated.
 */
export function clearSelections(platformId: string): void {
  try {
    const userPrefix = `${STORAGE_KEY_PREFIX}:${platformId}:`;
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(userPrefix)) keys.push(key);
    }
    keys.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

/**
 * Read this user's persisted org selection for the current login and tab, or
 * null — including after a new login, which is what resets the selection.
 */
export function readSelection(scope: SelectionScope): string | null {
  try {
    return sessionStorage.getItem(storageKey(scope));
  } catch {
    return null;
  }
}

/**
 * First-alphabetical-by-displayName default (AD-10). The BFF returns the list
 * pre-sorted, but we sort defensively rather than depend on response order.
 */
export function resolveDefault(
  entities: readonly CommonOrgEntity[]
): CommonOrgEntity | undefined {
  return [...entities].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  )[0];
}

/**
 * Dispatch the composed/bubbling `bridge-bar-context-changed` event from the
 * selector (AD-8). The bar root re-dispatches it on `document` (STEP-14) so
 * both adopter integration styles receive it on a single channel.
 */
export function dispatchContextChanged(
  target: EventTarget,
  detail: BridgeBarContextChangedDetail
): void {
  target.dispatchEvent(
    new CustomEvent(CONTEXT_CHANGED_EVENT, {
      detail,
      composed: true,
      bubbles: true
    })
  );
}
